import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority";
import { X } from "lucide-react"
import { Portal } from "@radix-ui/react-portal";
import { CheckCheck, XCircle, Info, AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"

const iconColorMap = {
  default: "text-green-600 dark:text-green-400",
  destructive: "text-white-600 dark:text-white-400",
  info: "text-blue-500 dark:text-blue-400",
  warning: "text-primary dark:text-primary",
}

const ToastVariantContext = React.createContext("default")

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <Portal container={props.containerRef?.current ?? undefined}>
    <ToastPrimitives.Viewport
      data-toast-viewport=""
      ref={ref}
      className={cn(
      "fixed top-0 right-0 z-[99999] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
          className
        )}
      {...props} />
    </Portal>
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border border-green-500 bg-background text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
        info: "border border-blue-400 bg-primary-tint text-foreground group",
        warning: "border border-primary bg-primary-tint text-foreground group",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const iconMap = {
  default: CheckCheck,
  destructive: XCircle,
  info: Info,
  warning: Info,
}

const Toast = React.forwardRef(({ className, variant, children, ...props }, ref) => {
  const Icon = iconMap[variant ?? "default"]

  return (
    <ToastVariantContext.Provider value={variant ?? "default"}>
      <ToastPrimitives.Root
        ref={ref}
        className={cn(
          toastVariants({ variant }),
          "flex items-start gap-2", // 👈 key change
          className
        )}
        {...props}
      >
        {/* ✅ Left icon */}
        <div className="mt-0.5">
          <Icon
            className={cn(
              "h-5 w-5 opacity-90",
              iconColorMap[variant ?? "default"]
            )}
          />
        </div>

        {/* ✅ Right content (title + description) */}
        <div className="flex flex-col gap-1 flex-1">
          {children}
        </div>
      </ToastPrimitives.Root>
    </ToastVariantContext.Provider>
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    )}
    {...props} />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    )}
    toast-close=""
    {...props}>
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn("text-sm opacity-90", className)} {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export { ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction };
